import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MagnifyingGlass, Sparkle, FilePdf, ArrowRight, ArrowsClockwise, CheckCircle,
  Users, ShieldCheck, Storefront, Question,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_META = {
  all:   { label: "All features",  icon: Sparkle,      color: "text-adb-navy" },
  user:  { label: "For end-users", icon: Users,        color: "text-zenith-teal" },
  admin: { label: "For admins",    icon: ShieldCheck,  color: "text-zenith-magenta" },
  sales: { label: "Product tour",  icon: Storefront,   color: "text-zenith-green" },
};

export default function Help() {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState("all");
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [role, setRole] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/help/manifest`, { timeout: 15000 });
      setManifest(data);
    } catch (e) {
      // network error is fine — we'll show a friendly error state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll once after 8s so LLM-enriched content lands without a manual refresh.
    const t = setTimeout(load, 8000);
    // Best-effort role lookup so we can show the super_admin regen button.
    const token = localStorage.getItem("token");
    if (token) {
      axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setRole(r.data?.role))
        .catch(() => {});
    }
    return () => clearTimeout(t);
  }, []);

  const filteredCategories = useMemo(() => {
    if (!manifest) return [];
    const q = query.trim().toLowerCase();
    return manifest.categories
      .map((cat) => ({
        ...cat,
        features: cat.features.filter((f) => {
          const audOk = audience === "all" || (f.audience || []).includes(audience);
          if (!audOk) return false;
          if (!q) return true;
          return (
            f.title.toLowerCase().includes(q) ||
            (f.summary || "").toLowerCase().includes(q) ||
            (f.endpoints || []).some((e) => e.toLowerCase().includes(q))
          );
        }),
      }))
      .filter((cat) => cat.features.length > 0);
  }, [manifest, audience, query]);

  const totalShown = filteredCategories.reduce((s, c) => s + c.features.length, 0);

  const onDownloadPdf = () => {
    window.open(`${API}/api/help/brochure.pdf`, "_blank");
  };

  const onRegenerate = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/help/regenerate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTimeout(load, 6000); // give background job a head-start
    } catch (e) {
      // silently ignore — regen is admin-only
    } finally {
      setTimeout(() => setRefreshing(false), 6000);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-zenith-teal font-bold">Product help center</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy mt-2">
            Everything Zenith360 HR <span className="font-serif-display italic text-zenith-magenta">can do.</span>
          </h1>
          <p className="text-sm text-zinc-600 mt-3 max-w-2xl">
            {manifest ? (
              <>
                <b>{manifest.total_features}</b> features across <b>{manifest.categories.length}</b> modules.
                Manifest auto-regenerates on every deploy — new features appear here automatically.
                {" "}
                {manifest.enriched ? (
                  <span className="inline-flex items-center gap-1 text-zenith-green font-semibold">
                    <CheckCircle size={13} weight="fill" /> AI copy ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-zenith-teal font-semibold animate-pulse">
                    <Sparkle size={13} weight="fill" /> AI enriching copy…
                  </span>
                )}
              </>
            ) : (
              <>Loading the latest product manifest…</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="help-toolbar">
          <Button
            onClick={onDownloadPdf}
            className="bg-adb-navy hover:bg-adb-navy-800 text-white rounded-sm"
            data-testid="help-download-pdf"
          >
            <FilePdf size={16} weight="fill" className="mr-1.5" /> Download brochure (PDF)
          </Button>
          {role === "super_admin" && (
            <Button
              variant="outline"
              onClick={onRegenerate}
              disabled={refreshing}
              className="border-zenith-magenta/40 text-zenith-magenta hover:bg-zenith-magenta/5 rounded-sm"
              data-testid="help-regenerate"
            >
              <ArrowsClockwise size={15} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Regenerate copy"}
            </Button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 sticky top-0 z-10 bg-white/85 backdrop-blur border-y border-zinc-200 py-3 -mx-4 px-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="help-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search features, endpoints, keywords…"
            className="pl-9 rounded-sm border-zinc-300"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="help-audience-tabs">
          {["all", "user", "admin", "sales"].map((a) => {
            const M = AUDIENCE_META[a];
            const Icon = M.icon;
            const active = audience === a;
            return (
              <button
                key={a}
                type="button"
                onClick={() => setAudience(a)}
                data-testid={`help-tab-${a}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-all border ${
                  active
                    ? "bg-adb-navy text-white border-adb-navy shadow-sm"
                    : "bg-white text-zinc-700 border-zinc-300 hover:border-adb-navy/40"
                }`}
              >
                <Icon size={13} weight="fill" /> {M.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="help-loading">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-sm" />)}
        </div>
      )}

      {!loading && manifest && (
        <div className="text-xs text-zinc-500">
          Showing <b>{totalShown}</b> of {manifest.total_features} features
          {audience !== "all" && <> · filtered to <b>{AUDIENCE_META[audience].label}</b></>}
        </div>
      )}

      {/* Category sections */}
      {!loading && filteredCategories.map((cat) => (
        <section key={cat.name} data-testid={`help-cat-${cat.name.replace(/\W+/g, "-").toLowerCase()}`}>
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="font-display text-2xl font-bold text-adb-navy tracking-tight">{cat.name}</h2>
            <span className="text-xs text-zinc-500">{cat.features.length} feature{cat.features.length === 1 ? "" : "s"}</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {cat.features.map((f) => <FeatureCard key={f.id} f={f} />)}
          </div>
        </section>
      ))}

      {!loading && manifest && filteredCategories.length === 0 && (
        <div className="text-center py-16 text-zinc-500" data-testid="help-empty">
          <Question size={40} className="mx-auto mb-3 text-zinc-300" />
          No features match your search. Try a different keyword or audience.
        </div>
      )}

      {/* Ask Zenith fallback */}
      <div className="border border-adb-navy/20 bg-gradient-to-br from-adb-navy/5 to-zenith-green/5 rounded-sm p-6">
        <div className="flex items-start gap-3">
          <Sparkle size={22} className="text-zenith-green" weight="fill" />
          <div>
            <div className="font-display text-lg font-bold text-adb-navy">Still stuck? Ask Zenith360.</div>
            <div className="text-sm text-zinc-600 mt-1">
              Click the <Badge variant="outline" className="rounded-sm bg-adb-navy text-white border-adb-navy inline">Ask Zenith360</Badge>
              {" "}button (bottom-right) — our AI copilot knows every feature listed above.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function FeatureCard({ f }) {
  const [open, setOpen] = useState(false);
  const audBadges = (f.audience || []).map((a) => (
    <span key={a} className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
      a === "user" ? "bg-zenith-teal/10 border-zenith-teal/40 text-zenith-teal" :
      a === "admin" ? "bg-zenith-magenta/10 border-zenith-magenta/40 text-zenith-magenta" :
      "bg-zenith-green/10 border-zenith-green/40 text-zenith-green"
    }`}>{a}</span>
  ));

  const screenshotUrl = `/help/${f.screenshot}`;

  return (
    <article
      className="rounded-sm border border-zinc-200 bg-white p-5 hover:border-adb-navy/40 hover:shadow-md transition-all group"
      data-testid={`help-feature-${f.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-display font-bold text-adb-navy text-base leading-snug">{f.title}</h3>
        <div className="flex flex-shrink-0 gap-1">{audBadges}</div>
      </div>
      {f.summary && <p className="text-sm text-zinc-600 leading-relaxed">{f.summary}</p>}

      {/* Screenshot with graceful fallback */}
      <div className="mt-3 rounded-sm overflow-hidden border border-zinc-200 bg-zinc-50 aspect-[16/9] grid place-items-center">
        <img
          src={screenshotUrl}
          alt={f.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
        />
        <div className="hidden flex-col items-center justify-center text-zinc-400 text-xs w-full h-full">
          <Sparkle size={22} className="mb-1" />
          Screenshot regenerating on next deploy
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-3 text-xs font-semibold text-adb-navy hover:text-zenith-teal transition-colors inline-flex items-center gap-1"
        data-testid={`help-feature-toggle-${f.id}`}
      >
        {open ? "Hide steps" : "Show steps & FAQ"} <ArrowRight size={11} className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t border-zinc-100 space-y-3 text-sm">
          {f.user_steps?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zenith-teal mb-1">For end-users</div>
              <ol className="list-decimal list-inside space-y-0.5 text-zinc-700 text-[13px]">
                {f.user_steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
          {f.admin_steps?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zenith-magenta mb-1">For admins</div>
              <ol className="list-decimal list-inside space-y-0.5 text-zinc-700 text-[13px]">
                {f.admin_steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
          {f.faqs?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">FAQ</div>
              <div className="space-y-2">
                {f.faqs.map((qa, i) => (
                  <div key={i}>
                    <div className="text-[13px] font-semibold text-zinc-800">Q. {qa.q}</div>
                    <div className="text-[13px] text-zinc-600">{qa.a}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {f.endpoints?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">APIs</div>
              <div className="flex flex-wrap gap-1">
                {f.endpoints.map((e) => (
                  <code key={e} className="text-[10.5px] px-1.5 py-0.5 bg-zinc-100 rounded-sm text-zinc-700">{e}</code>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {f.route && (
        <Link
          to={f.route}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-zenith-teal hover:text-adb-navy transition-colors"
          data-testid={`help-feature-open-${f.id}`}
        >
          Open {f.title} <ArrowRight size={11} />
        </Link>
      )}
    </article>
  );
}
